import {
    NetworkError,
    generateRouteHandler,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeBrowsePageIntent,
    type BrowsePageIntent,
} from './BrowsePageIntent';
import type { StaticCanonicalController } from '../get-static-canonical-url';
import type { IntentController } from '@jet/environment/dispatching';
import type {
    RouteProvider,
    UrlRuleDefinition,
} from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { Page } from '@jet-app/music-classical/models';
import { IntentKind } from '../common';
import { renderBrowsePage } from './renderBrowsePage';
import { requestViewPage } from '../../api/request-view-page';
import { BROWSE_TABS } from './types';

const canonicalUrlRule = {
    path: '/{storefront}/browse/{tab}',
    query: [],
};

const VALID_TABS = ['catalog', 'playlists', 'instruments'];

export const BrowsePageIntentController: IntentController<BrowsePageIntent> &
    RouteProvider &
    StaticCanonicalController<BrowsePageIntent> = {
    $intentKind: IntentKind.BROWSE_PAGE,

    routes: function (objectGraph: ObjectGraph) {
        const browsePageUrlRules: UrlRuleDefinition[] = [
            {
                path: '/browse',
                query: ['l?'],
            },
            {
                path: '/{storefront}/browse',
                query: ['l?'],
            },
            {
                path: '/browse/catalog',
                query: ['l?'],
            },
            {
                path: '/{storefront}/browse/catalog',
                query: ['l?'],
            },
            {
                path: '/browse/playlists',
                query: ['l?'],
            },
            {
                path: '/{storefront}/browse/playlists',
                query: ['l?'],
            },
            {
                path: '/browse/instruments',
                query: ['l?'],
            },
            {
                path: '/{storefront}/browse/instruments',
                query: ['l?'],
            },
        ];

        return [
            {
                rules: browsePageUrlRules,
                handler: (url, handlerParams) => {
                    // Attempt to set the tab intent value on direct page load
                    // otherwise page always defaults to Browse > Catalog
                    const segments = url.pathname.split('/');
                    const tab = segments[segments.length - 1];

                    if (VALID_TABS.includes(tab)) {
                        handlerParams.tab = tab;
                    }

                    return generateRouteHandler(
                        makeBrowsePageIntent,
                        objectGraph,
                    ).apply(this, [url, handlerParams]);
                },
            },
        ];
    },

    async perform(
        intent: BrowsePageIntent,
        objectGraph: ObjectGraph,
    ): Promise<Page> {
        // Default to Catalog tab for the /browse route to update canonical and 302 redirect
        const canonicalURL = this.staticCanonicalUrlFor(intent, objectGraph);

        const apiResponse = await requestViewPage(
            'browse',
            objectGraph,
            intent.tab,
        );

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const page = renderBrowsePage(
            canonicalURL,
            apiResponse,
            intent.tab,
            objectGraph,
        );

        return page;
    },

    staticCanonicalUrlFor(
        intent: BrowsePageIntent,
        objectGraph: ObjectGraph,
    ): string {
        return makeCanonicalUrl(
            canonicalUrlRule,
            {
                ...intent,
                tab: intent.tab ?? BROWSE_TABS.CATALOG,
            },
            objectGraph,
        );
    },
};
